import moment from "moment";

export const DEFAULT_MATERIAL_LIMIT = 100;

export const DEFAULT_REQUEST_HISTORY_DATE_RANGE = [
  moment().subtract(1, "month"),
  moment(),
];

export const materialBoxesConfig = [
  {
    key: "smallBox",
    label: "Cajas Pequeñas",
    accumulatorLabel: "Acumuladores Caja Pequeña",
    accumulatorName: "smallBoxAccumulator",
    qtyKey: "smallBoxQty",
    accumKey: "smallBoxAccum",
    accumCodeKey: "smallBoxAccumCode",
  },
  {
    key: "mediumBox",
    label: "Cajas Medianas",
    accumulatorLabel: "Acumuladores Caja Mediana",
    accumulatorName: "mediumBoxAccumulator",
    qtyKey: "mediumBoxQty",
    accumKey: "mediumBoxAccum",
    accumCodeKey: "mediumBoxAccumCode",
  },
  {
    key: "mediumPlusBox",
    label: "Cajas Medianas+",
    accumulatorLabel: "Acumuladores Caja Mediana+",
    accumulatorName: "mediumPlusBoxAccumulator",
    qtyKey: "mediumPlusBoxQty",
    accumKey: "mediumPlusBoxAccum",
    accumCodeKey: "mediumPlusBoxAccumCode",
  },
  {
    key: "largeBox",
    label: "Cajas Grandes",
    accumulatorLabel: "Acumuladores Caja Grande",
    accumulatorName: "largeBoxAccumulator",
    qtyKey: "largeBoxQty",
    accumKey: "largeBoxAccum",
    accumCodeKey: "largeBoxAccumCode",
  },
];


export const materialRequestFields = {
  contractNumber: undefined,
  clientNumber: undefined,
  labellerCode: undefined,
  temperatureRange: undefined,
  smallBox: undefined,
  smallBoxAccumulator: false,
  mediumBox: undefined,
  mediumBoxAccumulator: false,
  mediumPlusBox: undefined,
  mediumPlusBoxAccumulator: false,
  largeBox: undefined,
  largeBoxAccumulator: false,
  observations: undefined,
};

export const materialRequestMaterialsFields = {
  temperatureRange: undefined,
  smallBox: undefined,
  smallBoxAccumulator: false,
  mediumBox: undefined,
  mediumBoxAccumulator: false,
  mediumPlusBox: undefined,
  mediumPlusBoxAccumulator: false,
  largeBox: undefined,
  largeBoxAccumulator: false,
  observations: undefined,
};

// PETICION: Devuelve el límite máximo de cajas o el valor por defecto.
export function getMaterialLimit(limit) {
  return Number.isInteger(limit) ? limit : DEFAULT_MATERIAL_LIMIT;
}

// PETICION: Mapea los rangos de temperatura permitidos con las opciones maestras.
export function getTemperatureRangeOptions(materialLimit, searchOptions) {
  return (materialLimit?.data?.temperatureRanges || [])
    .map((temperatureRange) =>
      searchOptions?.data?.tempRangeList?.find(
        (option) => option.key === String(temperatureRange),
      ),
    )
    .filter(Boolean)
    .sort((rangeA, rangeB) => Number(rangeA.key) - Number(rangeB.key))
    .map((option) => ({
      key: option.key,
      label: option.value,
    }));
}

// PETICION: Obtiene las cajas habilitadas para el contrato seleccionado.
export function getEnabledMaterialBoxes(materialLimit) {
  return materialBoxesConfig.filter(
    (box) => materialLimit?.data?.materials?.[box.key]?.limit != null,
  );
}

// PETICION: Obtiene los nombres de campos de cajas habilitadas.
export function getMaterialBoxFieldNames(materialLimit) {
  return getEnabledMaterialBoxes(materialLimit).map((box) => box.key);
}

// PETICION: Obtiene los campos obligatorios del formulario.
export function getRequiredMaterialRequestFieldNames(materialLimit) {
  return [
    "contractNumber",
    "clientNumber",
    "labellerCode",
    "temperatureRange",
    ...getMaterialBoxFieldNames(materialLimit),
  ];
}

// PETICION: Comprueba si un valor de formulario está informado.
export function isMaterialRequestFieldFilled(value) {
  if (value === undefined || value === null || value === "") {
    return false;
  }

  if (typeof value === "object" && "key" in value) {
    return value.key !== undefined && value.key !== null && value.key !== "";
  }

  return true;
}

// PETICION: Comprueba si al menos una caja tiene cantidad mayor que cero.
export function hasAnyMaterialBoxQtyGreaterThanZero(form, materialLimit) {
  return getMaterialBoxFieldNames(materialLimit).some((fieldName) => {
    const value = Number(form.getFieldValue(fieldName) || 0);

    return value > 0;
  });
}

// PETICION: Comprueba si al menos una caja habilitada tiene cantidad mayor que cero usando los valores del formulario.
export function hasAnyEnabledMaterialBoxQtyGreaterThanZero(
  values,
  materialLimit,
) {
  return getEnabledMaterialBoxes(materialLimit).some(
    (box) => Number(values?.[box.key] || 0) > 0,
  );
}

// PETICION: Obtiene hora y minuto actuales en horario peninsular.
export function getCurrentProcessingTimeParts() {
  const formatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(new Date());

  return {
    hour: Number(parts.find((part) => part.type === "hour")?.value || 0),
    minute: Number(parts.find((part) => part.type === "minute")?.value || 0),
  };
}

// PETICION: Determina si debe mostrarse el aviso previo al envío.
export function shouldShowDeferredProcessingNotice() {
  const { hour, minute } = getCurrentProcessingTimeParts();

  return hour > 13 || (hour === 13 && minute >= 30);
}

// PETICION: Comprueba si el formulario tiene errores de validación.
export function hasMaterialRequestFormErrors(form) {
  const fieldErrors = form.getFieldsError();

  return Object.keys(fieldErrors).some(
    (fieldName) => fieldErrors[fieldName] && fieldErrors[fieldName].length > 0,
  );
}

// PETICION: Determina si debe deshabilitarse el botón de confirmación.
export function shouldDisableMaterialRequestSubmit(form, materialLimit) {
  if (materialLimit.status !== "loaded") {
    return true;
  }

  const requiredFieldNames =
    getRequiredMaterialRequestFieldNames(materialLimit);

  const areRequiredFieldsFilled =
    requiredFieldNames.length > 0 &&
    requiredFieldNames.every((fieldName) =>
      isMaterialRequestFieldFilled(form.getFieldValue(fieldName)),
    );

  const hasAnyRequestedBox = hasAnyMaterialBoxQtyGreaterThanZero(
    form,
    materialLimit,
  );

  const hasErrors = hasMaterialRequestFormErrors(form);

  return !areRequiredFieldsFilled || !hasAnyRequestedBox || hasErrors;
}

// PETICION: Normaliza una cantidad numérica del formulario.
export function getQtyValue(value) {
  return Number(value || 0);
}

// PETICION: Construye el payload para crear una petición de material.
export function buildMaterialRequestPayload(values, materialLimit) {
  return {
    contractNumber: materialLimit.data.customer.contractNumber,
    clientNumber: materialLimit.data.customer.clientNumber,
    labellerCode: materialLimit.data.customer.labellerCode,
    temperatureRange: parseInt(values.temperatureRange?.key),
    smallBoxQty: getQtyValue(values.smallBox),
    smallBoxAccum: !!values.smallBoxAccumulator,
    mediumBoxQty: getQtyValue(values.mediumBox),
    mediumBoxAccum: !!values.mediumBoxAccumulator,
    mediumPlusBoxQty: getQtyValue(values.mediumPlusBox),
    mediumPlusBoxAccum: !!values.mediumPlusBoxAccumulator,
    largeBoxQty: getQtyValue(values.largeBox),
    largeBoxAccum: !!values.largeBoxAccumulator,
    observations: values.observations || "",
  };
}

// BÚSQUEDA: Resetea valores y opciones dependientes de un formulario.
export function resetDependentFields(fields, setter, setFieldsValue) {
  const resetValues = {};
  const resetOptions = {};

  fields.forEach((field) => {
    resetValues[field] = undefined;
    resetOptions[`${field}Options`] = [];
  });

  setter((prev) => ({
    ...prev,
    ...resetOptions,
  }));

  setFieldsValue(resetValues);
}

// BÚSQUEDA: Obtiene las opciones únicas de contrato desde los perfiles.
export function getContractOptions(userRoles) {
  const contracts = [];

  if (!userRoles?.data) return contracts;

  userRoles.data.forEach((profile) => {
    if (
      profile?.contractNumber &&
      !contracts.includes(profile.contractNumber)
    ) {
      contracts.push(profile.contractNumber);
    }
  });

  return contracts;
}

// BÚSQUEDA: Obtiene los detallables disponibles para un contrato.
export function getClientOptions(userRoles, contract) {
  const clients = [];

  if (!userRoles?.data || !contract) return clients;

  userRoles.data
    .filter((profile) => profile.contractNumber === contract)
    .forEach((profile) => {
      if (profile?.clientNumber && !clients.includes(profile.clientNumber)) {
        clients.push(profile.clientNumber);
      }
    });

  return clients;
}

// BÚSQUEDA: Obtiene los etiquetadores disponibles para un detallable.
export function getLabellerOptions(userRoles, clientNumber) {
  const labellers = [];

  if (!userRoles?.data || !clientNumber) return labellers;

  userRoles.data
    .filter((profile) => profile.clientNumber === clientNumber)
    .forEach((profile) => {
      if (profile?.labellerCode && !labellers.includes(profile.labellerCode)) {
        labellers.push(profile.labellerCode);
      }
    });

  return labellers;
}

// BÚSQUEDA: Devuelve un moment o un valor de fallback.
export function getMomentDate(value, fallback) {
  return value ? moment(value) : fallback;
}

// BÚSQUEDA: Construye el rango de fechas del buscador desde Redux.
export function buildDateRangeFromSearchParams(searchParams) {
  return [
    getMomentDate(
      searchParams?.startDate,
      DEFAULT_REQUEST_HISTORY_DATE_RANGE[0],
    ),
    getMomentDate(searchParams?.endDate, DEFAULT_REQUEST_HISTORY_DATE_RANGE[1]),
  ];
}

// BÚSQUEDA: Comprueba si están seleccionados contrato, detallable y etiquetador.
export function hasRequiredContractFields({
  contractNumber,
  clientNumber,
  labellerCode,
}) {
  return Boolean(contractNumber && clientNumber && labellerCode);
}

// BÚSQUEDA: Limita el rango del DatePicker al último año y hasta hoy.
export function disabledRequestHistoryDate(current) {
  const today = moment().endOf("day");
  const oneYearAgo = moment().subtract(1, "year").startOf("day");

  return current && (current > today || current < oneYearAgo);
}

// TABLA: Devuelve "N/D" cuando un valor no está informado.
export function checkMaterialHistoryValue(value) {
  if (value === undefined || value === null || value === "") return "N/D";

  return value;
}

// TABLA: Formatea la fecha de petición para la tabla.
export function formatMaterialRequestDate(value) {
  if (!value) return "N/D";

  return moment(value).format("DD/MM/YYYY HH:mm:ss");
}

// TABLA: Convierte un booleano en Sí/No.
export function formatYesNo(value) {
  return value ? "Sí" : "No";
}

// TABLA: Construye una línea de material solicitado.
export function buildMaterialHistoryLine(label, qty, accum) {
  const quantity = Number(qty || 0);

  if (quantity <= 0) return null;

  return `${label}: ${quantity} Acumuladores: ${formatYesNo(accum)}`;
}

// TABLA: Construye las líneas de materiales solicitados de una fila.
export function getMaterialHistoryLines(row) {
  return materialBoxesConfig
    .map((box) =>
      buildMaterialHistoryLine(box.label, row[box.qtyKey], row[box.accumKey]),
    )
    .filter(Boolean);
}

// TABLA: Devuelve las líneas junto con los datos necesarios para resolver su manual.
export function getMaterialHistoryItems(row) {
  return materialBoxesConfig
    .map((box) => {
      const line = buildMaterialHistoryLine(
        box.label,
        row?.[box.qtyKey],
        row?.[box.accumKey],
      );

      if (!line) return null;

      return {
        line,
        boxKey: box.key,
        accumulatorCode: row?.[box.accumCodeKey] || null,
      };
    })
    .filter(Boolean);
}

// TABLA: Determina si la tabla de histórico está cargando.
export function isRequestHistoryLoading(requestHistory) {
  return requestHistory?.isLoading || requestHistory?.status === "loading";
}

// TABLA: Determina si debe mostrarse la tabla con resultados.
export function shouldShowRequestHistoryTable(requestHistory) {
  const data = requestHistory?.data || [];

  return (
    requestHistory?.searchWasMade === true &&
    requestHistory?.statusCode === 200 &&
    data.length > 0
  );
}

// TABLA: Determina si debe mostrarse un mensaje en lugar de la tabla.
export function shouldShowRequestHistoryMessage(requestHistory) {
  const data = requestHistory?.data || [];

  return (
    requestHistory?.searchWasMade === true &&
    requestHistory?.status === "loaded" &&
    (requestHistory?.statusCode !== 200 || data.length === 0)
  );
}
